// src/components/property/WizardNavigationBar.tsx
/**
 * WizardNavigationBar Component
 *
 * Consistent Back/Forward navigation bar for wizard steps.
 * Shows contextual buttons based on current step.
 */

import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { IWizardNavigationBarProps } from '@/@types/property';

function WizardNavigationBar({
  currentStep,
  onBack,
  onContinue,
  continueLabel = 'Continue',
  backLabel = 'Back',
  isContinueDisabled = false,
  isContinueLoading = false,
}: IWizardNavigationBarProps) {
  const showBack = currentStep > 1 && onBack;
  const showContinue = currentStep < 6 && onContinue;

  if (!showBack && !showContinue) {
    return null;
  }

  return (
    <div className="flex items-center justify-between mt-6 pt-6 border-t">
      <div>
        {showBack && (
          <Button variant="outline" onClick={onBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {backLabel}
          </Button>
        )}
      </div>
      <div>
        {showContinue && (
          <Button
            onClick={onContinue}
            disabled={isContinueDisabled || isContinueLoading}
            className="gap-2"
          >
            {isContinueLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Loading...
              </>
            ) : (
              <>
                {continueLabel}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

export { WizardNavigationBar };
